import {
  Anchor,
  Baby,
  BarChart3,
  FerrisWheel,
  Gamepad2,
  Globe,
  Headphones,
  Landmark,
  Megaphone,
  Mountain,
  RefreshCw,
  Rocket,
  Snowflake,
  Target,
  Ticket,
  TrendingUp,
  WalletCards,
  Waves,
  Zap
} from 'lucide-react';

export const data = {
  heroData: {
    heading: <>Increase Ticket Sales for <br className="hidden lg:block" /><span className="">Your Adventure Park</span></>,
    subHeading: "Get more visitors, increase online bookings, and grow your attraction with complete marketing, ticketing, and visitor management support from Greet.",
    badgeText: "Grow your attraction with Greet",
    features: [
      { text: 'Increase Online Ticket Sales', icon: TrendingUp },
      { text: 'End-to-End Marketing & Promotion Support', icon: Megaphone },
      { text: 'Social Media & Paid Ads Support', icon: Target },
      { text: 'High-Converting Website & Booking Experience', icon: Globe },
      { text: 'Real-Time Sales & Visitor Analytics', icon: BarChart3 },
      { text: 'Dedicated Growth & Operational Support', icon: Headphones },
    ]
  },
  problemData: {
    heading: "Struggling to Increase Ticket Sales & Visitor Footfall?",
    subHeading: "Without the right technology and marketing support, growing visitor numbers becomes difficult. Greet helps attractions improve online bookings, simplify operations, and create better visitor experiences that drive repeat customers and higher revenue.",
    painPoints: [
      'Low online ticket bookings',
      'Poor visibility and digital reach',
      'Visitors dropping off during booking',
      'Slow or frustrating entry experience',
      'Limited analytics on visitor behavior',
      'Difficulty managing promotions & offers',
      'No centralized customer insights',
      'High dependence on seasonal traffic',
    ],
    footerText: "Greet removes all these barriers - so you can focus on growing your attraction, not chasing bookings."
  },
  featureData: {
    heading: "How Greet Helps Attractions Grow",
    subHeading: "From marketing and promotions to smarter booking experiences and visitor analytics, Greet helps attractions increase ticket sales and grow faster.",
    features: [
      { id: 1, name: 'Increase Online Ticket Sales', desc: 'Improve booking conversion and help more visitors discover your attraction online.', icon: TrendingUp },
      { id: 2, name: 'End-to-End Marketing Support', desc: 'Get support for promotions, campaigns, seasonal offers, and attraction visibility.', icon: Megaphone },
      { id: 3, name: 'Social Media & Paid Ads Support', desc: 'Reach more customers through digital campaigns, social media promotions, and paid advertising support.', icon: Target },
      { id: 4, name: 'High-Converting Website & Booking Experience', desc: 'Deliver a smoother and faster booking journey designed to improve visitor conversion.', icon: Globe },
      { id: 5, name: 'Smarter Online & Offline Ticketing', desc: 'Manage online bookings, walk-ins, and group bookings through one platform.', icon: Ticket },
      { id: 6, name: 'Real-Time Sales & Visitor Analytics', desc: 'Track bookings, visitor trends, campaign performance, and operational insights anytime.', icon: BarChart3 },
      { id: 7, name: 'Built for High-Footfall Attractions', desc: 'Designed specifically for waterparks, amusement parks, gaming zones, and attractions.', icon: Rocket },
      { id: 8, name: 'Dedicated Growth & Operational Support', desc: 'Our team helps attractions with growth planning, operations, and ongoing optimization.', icon: Headphones },
      { id: 9, name: 'Better Visitor Retention & Repeat Bookings', desc: 'Create smoother visitor experiences that encourage repeat visits and customer loyalty.', icon: RefreshCw },
    ]
  },
  industriesData: {
    heading: "Who We Help Grow",
    subHeading: "From entertainment venues to seasonal parks, Greet powers exceptional guest experiences.",
    industries: [
      {
        title: 'Waterparks',
        description: 'Manage online bookings, QR-based visitor entry, and high-footfall operations smoothly through one centralized platform.',
        icon: Waves,
      },
      {
        title: 'Amusement Parks',
        description: 'Handle ticket sales, crowd management, and visitor operations efficiently during weekends, holidays, and seasonal rush.',
        icon: FerrisWheel,
      },
      {
        title: 'Gaming Zones & Arcades',
        description: 'Simplify bookings, walk-ins, timed entries, and customer management for indoor entertainment and gaming experiences.',
        icon: Gamepad2,
      },
      {
        title: 'Adventure Parks',
        description: 'Manage adventure activities, visitor entry, ticketing, and operational flow seamlessly through one powerful system.',
        icon: Mountain,
      },
      {
        title: 'Museums & Attractions',
        description: 'Enable organized visitor management, digital bookings, and smoother guest experiences for exhibitions and attractions.',
        icon: Landmark,
      },
      {
        title: 'Adventure Trips & Tour Packages',
        description: 'Manage bookings, itineraries, group experiences, and traveler operations seamlessly.',
        icon: Snowflake,
      },
      {
        title: 'Family Entertainment Centers',
        description: 'Manage kids play zones, indoor attractions, and entertainment centers with streamlined bookings and visitor management.',
        icon: Baby,
      },
      {
        title: 'Water Sports & Activities',
        description: 'Simplify activity bookings, visitor coordination, and ticketing for boating, water sports, and adventure experiences.',
        icon: Anchor,
      },
    ]
  },
  primaryCtaData: {
    heading: "Ready to experience the difference?",
    buttonText: "Let's Talk, We're Way Better"
  },
  metricsData: {
    metrics: [
      { value: '10M+', label: 'Transactions' },
      { value: '1,00,000+', label: 'Tickets Sold' },
      { value: '5,000+', label: 'Successful Events Managed' },
      { value: '35+', label: 'Cities & Counting', desc: 'Trusted by organizers across India.' },
      { value: '50+', label: 'Live Attractions', desc: 'Waterparks, museums, parks, etc.' },
      { value: '4.8★', label: 'Satisfaction Score', desc: 'Backed by real organizers and institutions.' },
    ]
  },
  testimonialsData: {
    heading: "What Attraction Owners Say About Greet",
    subHeading: "Real experiences from waterparks, amusement parks, and attractions using Greet to manage ticketing, operations, and visitor experiences.",
    testimonials: [
      { id: 1, title: 'Launching our online booking system was much easier than expected.', description: 'Greet helped us set up ticketing, visitor entry, and our website within days. The team handled everything smoothly, and the support has been excellent throughout.', author: 'Operations Manager', role: 'Exotica Club', rating: 5 },
      { id: 2, title: 'The QR entry system made weekends much smoother.', description: 'Earlier, we struggled with long queues during peak hours. After switching to Greet, visitor entry became much faster and easier to manage.', author: 'Owner', role: 'Entertainment Kingdom', rating: 5 },
      { id: 3, title: "We didn't need to hire any developers.", description: 'The biggest advantage for us was that Greet already had a ready platform built for attractions. We launched quickly without spending lakhs on software development.', author: 'Founder', role: 'Akhela Water Sports', rating: 5 },
      { id: 4, title: 'Their support team actually stays connected.', description: 'Most companies disappear after onboarding. With Greet, their operations and tech team still helps us with pricing updates, website changes, and day-to-day support whenever needed.', author: 'Manager', role: 'SSB Kingdom', rating: 5 },
      { id: 5, title: 'The analytics dashboard helps us track everything live.', description: "We can now monitor ticket sales, visitor flow, and peak hours directly from the app. It's helped us make much better operational decisions.", author: 'Manager', role: 'Tourist Attraction & Park', rating: 5 },
      { id: 6, title: 'Greet also helped us improve our online visibility.', description: 'Apart from ticketing, their team guided us with promotions and digital marketing support which helped increase our bookings during weekends and holidays.', author: 'Marketing Head', role: 'SSB Gamezone', rating: 5 },
      { id: 7, title: 'Managing online and offline ticket sales became simple.', description: 'Before Greet, we used separate systems for counter sales and online bookings. Now everything is managed from one platform, which saves our team a lot of time.', author: 'Operations Team', role: 'Theme Park', rating: 5 },
      { id: 8, title: 'The platform handled heavy visitor traffic smoothly.', description: 'During festivals and holiday rush, the system remained fast and stable. That reliability is very important for attractions like ours.', author: 'Owner', role: 'Seasonal Attraction & Waterpark', rating: 5 },
    ]
  },
  comparisonData: {
    heading: "Why Attractions Choose Greet Over Traditional Platforms",
    subHeading: "A quick comparison of Greet vs traditional ticketing and management platforms.",
    comparisons: [
      { feature: 'Growth Focus', icon: TrendingUp, greet: 'Built to increase bookings & visitor footfall', competitor: 'Mostly limited to ticketing operations' },
      { feature: 'Marketing Support', icon: Megaphone, greet: 'Complete marketing & promotional support', competitor: 'Usually not provided' },
      { feature: 'Social Media & Ads', icon: Target, greet: 'Social media campaigns & paid ads support', competitor: 'Requires separate agencies' },
      { feature: 'Website Conversion', icon: Globe, greet: 'High-converting booking & website experience', competitor: 'Outdated or low-converting booking flow' },
      { feature: 'Ticketing Experience', icon: Ticket, greet: 'Smarter online & offline ticketing', competitor: 'Fragmented or manual systems' },
      { feature: 'Visitor Experience', icon: Zap, greet: 'Faster QR-based visitor entry', competitor: 'Slower queues and operational delays' },
      { feature: 'Analytics & Insights', icon: BarChart3, greet: 'Real-time visitor & sales analytics', competitor: 'Limited reporting visibility' },
      { feature: 'Dedicated Support', icon: Headphones, greet: 'Dedicated growth & operational support', competitor: 'Basic ticket-based support' },
      { feature: 'Scalability', icon: Rocket, greet: 'Built for high-footfall attractions', competitor: 'May struggle during peak traffic' },
      { feature: 'Revenue Opportunities', icon: WalletCards, greet: 'Better promotions, offers & booking conversion', competitor: 'Limited growth-focused tools' },
    ]
  },
  secondaryCtaData: {
    heading: "Ready to Increase Ticket Sales?",
    subHeading: "Let Greet help you increase ticket sales, improve visitor experiences, and attract more customers through smarter marketing and ticketing solutions.",
    buttonText: "Talk to Our Team"
  },
  eventsData: {
    events: [
      {
        id: 1,
        poster: "https://greet.fydo.in/_next/image?url=https%3A%2F%2Fdpak4w7tblqv8.cloudfront.net%2FEVENTS%2F1744638553397_1706606350134_Get%252520High%252520on%252520Entertainment%25252010%252520by%25252020%252520feet%252520copy%252520%2525281%252529.webp&w=1080&q=75",
        name: "Entertainment Kingdom Sambalpur",
        eventAddress: "Silat, Odisha",
        link: "https://greet.fydo.in/entertainment-kingdom-sambalpur",
      },
      {
        id: 2,
        poster: "https://greet.fydo.in/_next/image?url=https%3A%2F%2Fdpak4w7tblqv8.cloudfront.net%2FEVENTS%2F1746426864119_SSB%2520waterpark_25x10%2520%25282%2529%2520%25281%2529.jpg&w=1080&q=75",
        name: "SSB Kingdom Water Park",
        eventAddress: "Ghodabara, Odisha",
        link: "https://greet.fydo.in/ssb-kingdom-water-park",
      },
      {
        id: 3,
        poster: "https://greet.fydo.in/_next/image?url=https%3A%2F%2Fdpak4w7tblqv8.cloudfront.net%2FEVENTS%2F1749827612515_Exotica%2520Aloha%2521%252025X10.jpg&w=1080&q=75",
        name: "TheExoticaAIohaClub",
        eventAddress: "Cuttack, Odisha",
        link: "https://greet.fydo.in/theexoticaalohaclub",
      },
      {
        id: 4,
        poster: "https://greet.fydo.in/_next/image?url=https%3A%2F%2Fdpak4w7tblqv8.cloudfront.net%2FEVENTS%2F1746248345794_SSB%2520Game%2520zone_25x10%2520%25281%2529%2520%25281%2529.jpg&w=1080&q=75",
        name: "SSB Game Zone",
        eventAddress: "Cuttack, Odisha",
        link: "https://greet.fydo.in/ssb-game-zone",
      },
      {
        id: 5,
        poster: "https://greet.fydo.in/_next/image?url=https%3A%2F%2Fdpak4w7tblqv8.cloudfront.net%2FEVENTS%2F1745409204479_banner-45x20-v1-aprl-22.jpg&w=1080&q=75",
        name: "Blue Splash Water Park Puri",
        eventAddress: "Puri, Odisha",
        link: "https://greet.fydo.in/blue-splash-water-park",
      },
      {
        id: 6,
        poster: "https://greet.fydo.in/_next/image?url=https%3A%2F%2Fdpak4w7tblqv8.cloudfront.net%2FEVENTS%2F1738946798438_WhatsApp%2520Image%25202025-02-07%2520at%25207.47.52%2520PM.jpeg&w=1080&q=75",
        name: "Akhela Water Sports",
        eventAddress: "Sambalpur, Odisha",
        link: "https://greet.fydo.in/akhela-water-sports",
      },
    ],
  }
};
